import React from 'react';
import fr from 'facet-rest';
import {
  FDBox, FDContentContainer,
  FDStack,
} from '@facet-wealth/design';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import ScenarioQuestionPicker
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/ScenarioQuestionPicker';
import { RightColumnProvider } from 'Context/RightColumnContext';
import { RoadmapDeliverableLeftNav } from 'Components/UI/roadmapDeliverable/leftNav';
import { dataAttrs } from 'Helpers/Testing';
import { RoadmapDeliverableFooterNav } from './RoadmapDeliverableFooterNav';
import { NavPicker } from './NavPicker';
import { RoadmapDeliverableHeader } from './RoadmapDeliverableHeader';

export const RoadmapDeliverableWrapperLayout = ({
  children,
  handleNextOverride = undefined,
  handlePreviousOverride = undefined,
  primaryActionLabelOverride = undefined,
  secondaryActionLabelOverride = undefined,
  clickableTextRel = undefined,
  disabled = false,
  pushNavToBottom = true,
  forceFullHeight = false,
}) => {
  const [loading, setLoading] = React.useState(false);
  useBoneBackgroundColor();

  const { displayErrorMessage } = useSnackbarContext();
  const { resource } = useMetaLayoutContext();
  const { setPageContentCollection, loadPageContentLink } = usePageContentContext();

  const canGoNext = resource.meta().canSubmit() || handleNextOverride;
  const handleNext = async () => {
    try {
      setLoading(true);
      if (handleNextOverride) {
        await handleNextOverride();
      }
      if (!handleNextOverride) {
        const nextStepColl = await resource.submit();
        setPageContentCollection(nextStepColl);
      }
    } catch (err) {
      displayErrorMessage(err, err.errors || 'Failed to continue. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClickableTextRel = async () => {
    try {
      setLoading(true);
      const nextStepColl = await clickableTextRel.link('related').get();
      setPageContentCollection(nextStepColl);
    } catch (err) {
      displayErrorMessage(err, err.errors || 'Failed to continue. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  const maybeHandlePrevious = resource.withRelationship(fr.relationships.previousLink.name,
    (rel) => async () => loadPageContentLink(rel.link('related')),
    () => null);

  const canGoBack = maybeHandlePrevious || handlePreviousOverride;

  const handlePrevious = async () => {
    if (!maybeHandlePrevious && !handlePreviousOverride) {
      return;
    }
    try {
      setLoading(true);
      if (handlePreviousOverride) {
        await handlePreviousOverride();
      }
      if (!handlePreviousOverride) {
        await maybeHandlePrevious();
      }
    } catch (err) {
      displayErrorMessage(err, err.errors || 'Failed to continue. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  const header = (resource.attr('title') || resource.attr('stepper')) ? <RoadmapDeliverableHeader resource={resource} /> : null;

  return (
    <RightColumnProvider>
      <FDContentContainer maxWidth={'xl'}>
        <FDStack direction={'column'} justifyContent={'center'} width={'100%'} height={forceFullHeight ? '100vh' : '100%'} minHeight={'100vh'}>
          <NavPicker resource={resource} />
          <FDStack direction={'column'} alignItems={'center'} spacing={4} flexGrow={1} width={'100%'} {...dataAttrs(`${resource.type()}-layout`)}>
            {header}
            {children}
          </FDStack>
          <ScenarioQuestionPicker />
          {/* Spacer box */}
          {pushNavToBottom && <FDBox flexGrow={1} />}
          <RoadmapDeliverableFooterNav
            disabled={disabled}
            onNext={canGoNext ? handleNext : null}
            loading={loading}
            primaryActionLabel={primaryActionLabelOverride || resource.attr('primaryActionLabel') || 'Continue'}
            secondaryActionLabel={secondaryActionLabelOverride || resource.attr('secondaryActionLabel') || 'Go Back'}
            onPrevious={canGoBack ? handlePrevious : null}
            clickableTextRel={clickableTextRel}
            onClickableTextRel={clickableTextRel ? handleClickableTextRel : null}
          />
          <RoadmapDeliverableLeftNav />
        </FDStack>
      </FDContentContainer>
    </RightColumnProvider>
  );
};
